import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { Box, Button, Stack, Typography } from "@mui/material";
import { Component, type ReactNode } from "react";

import { APP_UI } from "../ui/appUi";
import { clearPendingLazyStartSection, type LazyStartSection } from "../services/lazyPageLoaders";
import { failRuntimeActivity } from "../services/runtimeActivityService";

interface Props {
  section: LazyStartSection;
  label: string;
  onBack: () => void;
  children: ReactNode;
}
interface State { error: Error | null; }

export default class LazyAreaBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State { return { error }; }

  componentDidCatch(error: Error): void {
    failRuntimeActivity(`lazy:${this.props.section}`, error, `${this.props.label} konnte nicht geladen werden`);
  }

  componentDidUpdate(previous: Props): void {
    if (previous.section !== this.props.section && this.state.error) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Stack spacing={1.2} sx={{ minHeight: "42dvh", alignItems: "center", justifyContent: "center", px: 2, textAlign: "center" }}>
        <Box sx={{ width: 46, height: 46, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "rgba(244,163,64,.09)", border: "1px solid rgba(244,163,64,.28)" }}>
          <ErrorOutlineRoundedIcon sx={{ color: APP_UI.warning }} />
        </Box>
        <Typography sx={{ color: APP_UI.text, fontWeight: 900 }}>{this.props.label} konnte nicht geladen werden</Typography>
        <Typography variant="caption" sx={{ color: APP_UI.textMuted, maxWidth: 420 }}>
          Der Code-Split passt möglicherweise noch nicht zum gerade gewechselten App-/Service-Worker-Stand. Die PWA bleibt geöffnet.
        </Typography>
        <Stack direction="row" spacing={0.8}>
          <Button
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            onClick={() => window.location.reload()}
            sx={{ borderRadius: 999, textTransform: "none", color: APP_UI.accent, borderColor: APP_UI.accentBorder }}
          >
            Erneut laden
          </Button>
          <Button
            variant="text"
            onClick={() => { clearPendingLazyStartSection(this.props.section); this.props.onBack(); }}
            sx={{ borderRadius: 999, textTransform: "none", color: APP_UI.textMuted }}
          >
            Zurück
          </Button>
        </Stack>
      </Stack>
    );
  }
}
